import React, { useEffect } from 'react';
import { Button, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { ProblemInput } from './ProblemInput';
import { SearchResults } from './SearchResults';
import { CreateGuideDrawer } from './CreateGuideDrawer';
import { ErrorBoundary } from '@/shared/components/ErrorBoundary';
import { useProblemSearch, SearchStep } from '../hooks/useProblemSearch';
import { useGuideCreation } from '@/features/guides/hooks/useGuideCreation';
import { useNotification } from '@/shared/hooks/useNotification';
import { useOptionalSearchContext } from '../context/SearchContext';

const { Text } = Typography;

export const ProblemSearch: React.FC = () => {
  const { t } = useTranslation();
  const { contextHolder, notification } = useNotification();
  const searchContext = useOptionalSearchContext();
  
  const handleSuccess = () => {
    notification.success(t('guides.createSuccess'));
  };
  
  const { creationState, closeCreationDialog, ...guideCreationProps } = useGuideCreation({
    onSuccess: handleSuccess
  });
  
  const {
    currentStep,
    problemDescription,
    vehicleData,
    searchResults,
    actions,
    generateSearchKey
  } = useProblemSearch();
  
  // Update SearchContext whenever local state changes (only in demo mode)
  useEffect(() => {
    if (searchContext) {
      searchContext.setProblemDescription(problemDescription);
      searchContext.setVehicleData(vehicleData);
    }
  }, [problemDescription, vehicleData, searchContext]);

  return (
    <>
      {contextHolder}
      <div style={{
        maxWidth: 700,
        margin: '0 auto',
        padding: 'clamp(12px, 4vw, 24px)',
        width: '100%',
        boxSizing: 'border-box'
      }}>
      {/* Back link above the white box - only show in SearchResults step */}
      {currentStep === SearchStep.SearchResults && (
        <div className="back-button-container" style={{ marginBottom: 16 }}>
          <Button
            type="link"
            icon={<Text style={{ marginRight: 4 }}>←</Text>}
            onClick={actions.handleBackToProblem}
            style={{
              padding: 0,
              height: 'auto',
              color: '#666',
              fontSize: 14
            }}
          >
            {t('demo.searchResults.backToProblem')}
          </Button>
        </div>
      )}

      <ErrorBoundary>
        <div
          className="search-container"
          style={{
            backgroundColor: '#FFF',
            padding: 'clamp(16px, 4vw, 24px)',
            borderRadius: 8,
            minHeight: 500,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            boxSizing: 'border-box'
          }}
        >
          {currentStep === SearchStep.ProblemInput && (
            <ProblemInput
              initialProblem={problemDescription}
              initialVehicle={vehicleData}
              onSubmit={actions.handleProblemSubmit}
            />
          )}

          {currentStep === SearchStep.SearchResults && (
            <SearchResults
              key={generateSearchKey()}
              problemDescription={problemDescription}
              vehicleData={vehicleData}
              results={searchResults}
              onSearchComplete={actions.handleSearchComplete}
              onBack={actions.handleBackToProblem}
              onStartOver={actions.handleStartOver}
            />
          )}
        </div>
      </ErrorBoundary>
      
      {/* No action buttons in ProblemInput step anymore */}
      
      {currentStep === SearchStep.SearchResults && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
          <Button
            className="start-over-button"
            size="large"
            onClick={actions.handleStartOver}
            style={{
              minWidth: 160,
              height: 48,
              borderRadius: 8,
              backgroundColor: '#e8e8e8',
              borderColor: '#e8e8e8',
              color: '#666'
            }}
          >
            {t('demo.searchResults.newTroubleshooting')}
          </Button>
        </div>
      )}
      
      <CreateGuideDrawer
        open={creationState.isOpen}
        onClose={closeCreationDialog}
        {...guideCreationProps}
      />
      </div>
    </>
  );
};